import { useEffect, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Checkbox, FormControlLabel, Link, MenuItem, TextField, Typography } from '@mui/material';
import Seo from '../components/Seo';
import DirectoryLayout, { DarkCard, DirectorySearch, INK, LightCard, ProCard, fmtChecked, useMessage } from '../components/directory/DirectoryLayout';
import directoryService from '../services/directoryService';
import { ConveyancerRegisterResults } from '../components/directory/RegisterResults';

const TOWNS = ['Harare', 'Bulawayo', 'Mutare', 'Masvingo', 'Gweru', 'Kwekwe', 'Marondera', 'Kadoma', 'Zvishavane', 'Chinhoyi'];

// What a conveyancer does for a buyer — a real order, so it is numbered.
const STEPS = [
  ['Checks who owns it', 'For a title deed, searches the Deeds Registry for the owner and any bonds or caveats. For a cession, confirms the seller’s rights with the council or developer.'],
  ['Drafts the agreement', 'Prepares the agreement of sale and holds your deposit in the firm’s trust account.'],
  ['Registers the transfer', 'Lodges the deed of transfer so the title is registered in your name, or has the cession to you recorded with the council or developer.'],
];

/**
 * Find a conveyancer (spec 2026-09-26 §5): Eqaya members the Law Society of
 * Zimbabwe lists as allowed to practise as a conveyancer this year.
 */
export default function FindConveyancerPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const town = params.get('town') || '';
  const notary = params.get('notary') === '1';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { message, busy, error: msgError } = useMessage();

  const qs = params.toString();
  useEffect(() => {
    let live = true;
    setLoading(true); setError('');
    directoryService.conveyancers(Object.fromEntries(new URLSearchParams(qs)))
      .then((res) => { if (live) setData(res.data); })
      .catch(() => { if (live) { setData(null); setError('We could not load conveyancers just now. Please try again in a moment.'); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [qs]);

  const setParam = (patch) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    return next;
  }, { replace: true });

  const items = data?.items || [];

  return (
    <>
      <Seo title="Find a conveyancer in Zimbabwe" description="Conveyancers the Law Society of Zimbabwe lists as allowed to transfer property this year." canonicalPath="/conveyancers" />
      <DirectoryLayout
        eyebrow="Property · Find a conveyancer"
        title="Find a conveyancer in good standing"
        lead="A conveyancer is a lawyer the High Court has admitted to transfer property. A conveyancer prepares the deed that puts a house or stand into your name at the Deeds Registry, and handles a cession of rights with the council or developer. Every conveyancer here is an Eqaya member we check against the Law Society of Zimbabwe register."
        explainer={(
          <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
            {STEPS.map(([title, body], i) => (
              <Box component="li" key={title} sx={{ display: 'flex', gap: 1.75, p: 2.25, borderRadius: 2, bgcolor: '#f1f5f4' }}>
                <Box aria-hidden sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: '50%', bgcolor: '#134e4a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{i + 1}</Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: INK }}>{title}</Typography>
                  <Typography sx={{ fontSize: 14, lineHeight: 1.45, color: 'text.secondary' }}>{body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        searchRow={(
          <DirectorySearch value={q} onChange={setQ} onSubmit={() => setParam({ q: q.trim() })} placeholder="Conveyancer’s name or law firm">
            <TextField select size="small" label="Town" value={town} onChange={(e) => setParam({ town: e.target.value })}
              sx={{ minWidth: 170, '& .MuiInputBase-root': { height: 54, borderRadius: '10px' } }}>
              <MenuItem value="">Anywhere</MenuItem>
              {TOWNS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <FormControlLabel
              sx={{ m: 0, height: 54, px: 1.75, border: '1.5px solid #94a3b8', borderRadius: '10px', bgcolor: '#fff', whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontWeight: 600 } }}
              control={<Checkbox checked={notary} onChange={(e) => setParam({ notary: e.target.checked ? '1' : '' })} />}
              label="Also a notary public"
            />
          </DirectorySearch>
        )}
        resultsTitle={`Eqaya members in ${town || 'Zimbabwe'}`}
        resultsNote={!loading && !error && items.length ? 'All in good standing today' : null}
        loading={loading}
        error={error || msgError}
        empty={!loading && !error && items.length === 0 ? (
          <Alert severity="info">{params.get('q') ? 'No Eqaya member matches this search yet. The Law Society register is searched below.' : 'No conveyancer has joined Eqaya here yet. Search a name to check the Law Society register.'}</Alert>
        ) : null}
        after={<ConveyancerRegisterResults register={data?.register} q={params.get('q') || ''} />}
        sidebar={(
          <>
            <DarkCard title="Choose your own conveyancer">
              <Typography sx={{ fontSize: 15, lineHeight: 1.55, color: '#e6f4f2' }}>
                The seller&apos;s agent may suggest one. You can use any conveyancer in good standing — pick one who acts for you, not only for the seller.
              </Typography>
            </DarkCard>
            <LightCard title="Are you a conveyancer?">
              <Typography sx={{ fontSize: 15, lineHeight: 1.5, color: 'text.secondary', mb: 1 }}>
                Be found by buyers of verified homes, at home and abroad, with a good-standing badge.
              </Typography>
              <Link component={RouterLink} to="/register?role=conveyancer" sx={{ fontWeight: 700 }}>Join Eqaya →</Link>
            </LightCard>
          </>
        )}
        footnote="Eqaya does not recommend a particular conveyancer. Standing is checked against the Law Society of Zimbabwe directory of legal practitioners, conveyancers and notaries public. It tells you they may practise this year; the directory does not publish complaints."
      >
        {items.map((c) => (
          <ProCard
            key={c.userId}
            name={c.displayName}
            photo={c.photoUrl}
            firm={[c.firm, c.town].filter(Boolean).join(', ')}
            badge={`In good standing with the Law Society · ${fmtChecked(c.standing?.checkedAt)}`}
            chips={['Conveyancer', ...(c.notary ? ['Notary public'] : []), ...(c.partner ? ['Partner'] : [])]}
            details={[c.towns?.length ? `Takes transfers in ${c.towns.join(', ')}` : null]}
            primary={{ label: busy === c.userId ? 'Opening…' : 'Ask for a transfer quotation', onClick: () => message(c.userId), disabled: busy === c.userId }}
            secondary={{ label: 'Message', onClick: () => message(c.userId), disabled: busy === c.userId }}
          />
        ))}
      </DirectoryLayout>
    </>
  );
}
